import axios, { AxiosInstance, AxiosResponse } from 'axios';

export interface LoyaltyCustomerState {
  cnpj: string;
  customerId: string;
  customerName?: string;
  phone?: string;
  email?: string;
  notificationsEnabled: boolean;
  lastOrderAt?: string;
  totalOrders: number;
  ordersSinceLastReward: number;
  ordersSinceLastRewardTimestamps?: string[];
  currentWeekSequence: number;
  lastWeekWithOrder?: string;
  lastInactivityNotificationAt?: string;
  lastProgressNotificationAt?: string;
  lastStreakRewardAt?: string;
  lastInactivityRewardAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ListLoyaltyCustomerParams {
  customerId?: string;
  notificationsEnabled?: boolean;
  cnpj?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const withAuthInterceptor = (client: AxiosInstance): AxiosInstance => {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return client;
};

class LoyaltyCustomerService {
  private client: AxiosInstance;

  constructor() {
    this.client = withAuthInterceptor(
      axios.create({
        baseURL: API_BASE_URL,
        timeout: 15000,
      }),
    );
  }

  private buildParams(params?: ListLoyaltyCustomerParams) {
    if (!params) return undefined;
    const result: Record<string, any> = {};
    if (params.customerId) {
      const trimmed = params.customerId.trim();
      if (trimmed) {
        result.customerId = trimmed;
      }
    }
    if (params.notificationsEnabled !== undefined) {
      result.notificationsEnabled = params.notificationsEnabled;
    }
    if (params.cnpj) {
      const digits = params.cnpj.replace(/\D/g, '');
      if (digits) {
        result.cnpj = digits;
      }
    }
    return result;
  }

  async list(params?: ListLoyaltyCustomerParams): Promise<LoyaltyCustomerState[]> {
    try {
      const response: AxiosResponse<LoyaltyCustomerState[]> = await this.client.get(
        '/admin/loyalty/customer-states',
        {
          params: this.buildParams(params),
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar clientes de fidelidade');
    }
  }

  async toggleNotifications(customerId: string, enable: boolean, options?: { cnpj?: string }): Promise<LoyaltyCustomerState> {
    try {
      const response: AxiosResponse<LoyaltyCustomerState> = await this.client.patch(
        `/admin/loyalty/customer-states/${customerId}/notifications`,
        { enable },
        {
          params:
            options?.cnpj && options.cnpj.trim()
              ? { cnpj: options.cnpj.replace(/\D/g, '') }
              : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar notificações do cliente');
    }
  }
}

export const loyaltyCustomerService = new LoyaltyCustomerService();
export default loyaltyCustomerService;
